import isEmpty from 'lodash/isEmpty';
import compact from 'lodash/compact';
import { getCurrentEnvForClientSide, PRODUCTION } from '@zg-rentals/rental-platform-config';

// Types
type QueryObjectType = { [key: string]: string };

type EncodedAddressType = { address: string | null; unit: string };

type DecodedAddressType = { pathname: string; unit: string };

type DecodedAddressReturnType = { name: string; unit: string };

const regex = /[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g;
const regexWithoutUnderscore = /[!"#$%&'()*+,-./:;<=>?@[\]^`{|}~]/g;
export const encodeFullAddressForUrl = (
  { address = '', unit = '' }: EncodedAddressType = <EncodedAddressType>{},
): string => {
  if (!address) {
    return '';
  }

  const cleanAddress = address
    .replaceAll(',', '_')
    .replace(regexWithoutUnderscore, '')
    .replaceAll(' ', '-')
    .toLowerCase();
  const cleanUnit = unit.replace(regex, '').trim();

  if (cleanUnit) {
    return encodeURIComponent(cleanAddress) + '/?unit=' + cleanUnit;
  }

  return encodeURIComponent(cleanAddress) + '/';
};

export const decodeFullAddressFromUrl = (
  { pathname = '', unit = '' }: DecodedAddressType = <DecodedAddressType>{},
): DecodedAddressReturnType => {
  if (!pathname) {
    return { name: '', unit: '' };
  }

  const splitPath = compact(pathname.split('/'));
  const rawAddress = splitPath[splitPath.length - 1];
  const cleanAddress = rawAddress.replaceAll('-', ' ').replaceAll('_', ',').toLowerCase();

  return { name: decodeURIComponent(cleanAddress), unit: decodeURIComponent(unit) };
};

export const parseFullAddressString = (
  fullAddress: string,
  logErrorFn?: (error: any) => void,
): {
  street: string;
  city: string;
  state: string;
  zip: string;
} => {
  // Parse address format: "street, city, state zip"
  const parts = fullAddress.split(',').map((part) => part.trim());

  if (parts.length < 3) {
    logErrorFn?.({
      message: `Invalid address format: ${fullAddress}`,
      fullAddress,
      partsCount: parts.length,
    });
    return { street: '', city: '', state: '', zip: '' };
  }

  const street = parts[0];
  const city = parts[1];

  // Last part contains "state zip", split by last space
  const stateZip = parts[2].trim();
  const lastSpaceIndex = stateZip.lastIndexOf(' ');

  if (lastSpaceIndex === -1) {
    logErrorFn?.({
      message: `Invalid state/zip format in address: ${fullAddress}`,
      fullAddress,
      stateZip,
    });
    return { street: '', city: '', state: '', zip: '' };
  }

  const state = stateZip.substring(0, lastSpaceIndex).trim();
  const zip = stateZip.substring(lastSpaceIndex + 1).trim();

  return { street, city, state, zip };
};

export const decodeAddressComponentsFromUrl = (
  { pathname = '', unit = '' }: DecodedAddressType = <DecodedAddressType>{},
  logErrorFn?: (error: any) => void,
): {
  name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  unit: string;
} => {
  const { name, unit: decodedUnit } = decodeFullAddressFromUrl({ pathname, unit });
  const components = parseFullAddressString(name, logErrorFn);
  return { name, ...components, unit: decodedUnit };
};

export const decodeResourceIdFromUrl = (pathname: string): string => {
  const tokens = pathname.split('/');
  if (tokens.length < 4) {
    return '';
  }
  return tokens[3];
};

export const queryObjectToQueryString = (queryObject: QueryObjectType): string => {
  if (!queryObject || isEmpty(queryObject)) {
    return '';
  }

  return (
    '?' +
    Object.keys(queryObject)
      .map((key) => key + '=' + queryObject[key])
      .join('&')
  );
};

// Returns a zillow.com URL scoped to the current environment so QA/staging links
// stay within the same environment rather than always pointing to production.
export function getZillowUrl(path: string): string {
  const isProduction = getCurrentEnvForClientSide() === PRODUCTION;
  const base = isProduction || typeof window === 'undefined' ? 'https://www.zillow.com' : window.location.origin;
  return `${base}${path}`;
}

export const queryStringToQueryObject = (queryString = ''): QueryObjectType => {
  if (!queryString) {
    return {};
  }
  const decodedQueryString: string = decodeURIComponent(queryString);
  const regex = /[?]/g;
  const queryParams = decodedQueryString.replace(regex, '').split('&');
  const queryObject: QueryObjectType = {};

  for (let i = 0; i < queryParams.length; i++) {
    const curr = queryParams[i];
    const [key, val] = curr.split('=');
    const hasHyphen = val.split('-').length > 1;

    if (hasHyphen) {
      const vals = val.split('-').join(',');
      queryObject[key] = vals;
    } else {
      queryObject[key] = val;
    }
  }

  return queryObject;
};
